import { cloneDeep } from "lodash";
import { Option } from "uikit";

import typedKeys from "../../utils/typedKeys";

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import devTranslation from "./dev.yml";

type TRecord<T> = Record<string, T>;
interface RecursiveDictionary extends TRecord<string | RecursiveDictionary> {}

export const ConstantLanguage = {
	UK: "uk",
	RU: "ru",
	EN: "en",
	TR: "tr",
	AZ: "az",
	RO: "ro",
	KA: "ka",
	PL: "pl",
} as const;
export type TypeLanguage = typeof ConstantLanguage;
export type TypeLanguageKey<T> = {
	[key in ValueLanguage]: T;
};
export type ValueLanguage =
	(typeof ConstantLanguage)[keyof typeof ConstantLanguage];

const MAP_LANGUAGES: ValueLanguage[] = [
	"en",
	"uk",
	"ru",
	"tr",
	"az",
	"ro",
	"ka",
	"pl",
];

export type Language = ValueLanguage;

interface LanguagePackage {
	name: string;
	translation: RecursiveDictionary;
	sortName: string;
}
type LanguagePackages = Record<Language, LanguagePackage>;

export const languagePackages: LanguagePackages = {
	uk: {
		name: "Українська",
		translation: devTranslation,
		sortName: "УК",
	},
	en: {
		name: "English",
		translation: devTranslation,
		sortName: "EN",
	},
	tr: {
		name: "Türkçe",
		translation: devTranslation,
		sortName: "TR",
	},
	az: {
		name: "Azərbaycan",
		translation: devTranslation,
		sortName: "AZ",
	},
	ru: {
		name: "Русский",
		translation: devTranslation,
		sortName: "РУ",
	},
	ro: {
		name: "Romanian",
		translation: devTranslation,
		sortName: "RO",
	},
	ka: {
		name: "Georgian",
		translation: devTranslation,
		sortName: "KA",
	},
	pl: {
		name: "Polski",
		translation: devTranslation,
		sortName: "PL",
	},
} as const;

export const languages: Language[] = typedKeys(languagePackages);
export const defaultLanguage: Language = ConstantLanguage.UK;

export const languageOptions: Option<Language>[] = typedKeys(
	languagePackages,
).map((key) => ({
	key,
	value: key,
	label: languagePackages[key].name,
}));

export type RecordObject = Record<
	string,
	string | number | boolean | symbol | undefined | null
>;

type EntityByLang<T> = Partial<Record<Language, T>>;

export const createObjectFromConstantKye = <Key>(): Record<Language, Key> =>
	Object.values(ConstantLanguage).reduce((acc, key) => {
		acc[key.toLowerCase()] = "";
		return acc;
	}, {}) as Record<Language, Key>;

export const createObjectFromConstant = (): Record<Language, string> =>
	Object.values(ConstantLanguage).reduce((acc, key) => {
		acc[key.toLowerCase()] = "";
		return acc;
	}, {}) as Record<Language, string>;

export const createObjectLanguageNames = (
	name = "",
): Record<Language, string> =>
	languages.reduce((acc, key) => {
		acc[key] = name;
		return acc;
	}, createObjectFromConstant());

export const createObjectLanguageNamesFromObject = (
	value?: Partial<Record<Language, string>>,
): Record<Language, string> =>
	languages.reduce((acc, key) => {
		acc[key] = value?.[key] || "";
		return acc;
	}, createObjectFromConstant());

export const fillEntityByLang = <T>(
	defaultValue: T,
	entity: EntityByLang<T> = {},
): Record<Language, T> => {
	const result = { ...entity } as Record<Language, T>;

	for (const lang of MAP_LANGUAGES) {
		if (!(lang in result)) {
			result[lang] = defaultValue;
		}
	}

	return result;
};

export const fillFilterEntityByLang = <T extends object>(
	defaultValue: T,
	entity: EntityByLang<T> = {},
	langs: ValueLanguage[] | null = MAP_LANGUAGES,
): Record<Language, T> => {
	const result = { ...entity } as Record<Language, T>;

	const items = langs || MAP_LANGUAGES;

	for (const lang of items) {
		const current = entity[lang];

		if (!current) {
			result[lang] = cloneDeep(defaultValue);
		} else {
			const normalized = {} as T;

			for (const key of Object.keys(defaultValue) as (keyof T)[]) {
				normalized[key] =
					key in current
						? current[key]
						: cloneDeep(defaultValue[key]);
			}

			result[lang] = normalized;
		}
	}
	return result;
};

export const fillModifyEntityByLang = <T extends object>({
	defaultValue,
	entity = {},
	langs = MAP_LANGUAGES,
	isAddValue = false,
}: {
	defaultValue: T;
	entity?: EntityByLang<T>;
	langs?: ValueLanguage[] | null;
	isAddValue?: boolean;
}): Record<Language, T> => {
	const result = { ...entity } as Record<Language, T>;

	const items = langs || MAP_LANGUAGES;

	for (const lang of items) {
		const current = entity[lang];

		if (!current) {
			result[lang] = cloneDeep(defaultValue);
		} else {
			const normalized = {} as T;

			for (const key of Object.keys(defaultValue) as (keyof T)[]) {
				if (isAddValue) {
					normalized[key] =
						key in current
							? current[key] || cloneDeep(defaultValue[key])
							: cloneDeep(defaultValue[key]);
				} else {
					normalized[key] =
						key in current
							? current[key]
							: cloneDeep(defaultValue[key]);
				}
			}

			result[lang] = normalized;
		}
	}
	return result;
};

export const updateObjectLanguageNamesFromObject = (
	lang: Language,
	name: string,
	value: Partial<Record<Language, string>>,
): Record<Language, string> =>
	languages.reduce((acc, key) => {
		acc[key] = value?.[key] || name || "";
		if (lang === key) acc[key] = name || "";
		return acc;
	}, createObjectFromConstant());

export const createObjectLanguage = <
	Key extends string | number | boolean | RecordObject = RecordObject,
>(
	value: Key,
): Record<Language, Key> =>
	languages.reduce((acc, key) => {
		acc[key] = value;
		return acc;
	}, createObjectFromConstantKye<Key>());

export const createObjectFromConstantByType = <T>(): Record<Language, T> =>
	languages.reduce((acc, key) => {
		acc[key as Language] = {} as T;
		return acc;
	}, {} as Record<Language, T>);

export const addToObjectLanguage = <
	Value extends Record<Language, RecordObject>,
	Add extends Record<Language, RecordObject>,
>(
	value: Value,
	add: Add,
): Record<Language, Add & Value> =>
	languages.reduce((acc, key) => {
		acc[key] = {
			...(value?.[key] ?? {}),
			...(add?.[key] ?? {}),
		};
		return acc;
	}, createObjectFromConstantByType<any>());

export const addToObjectLanguageByAdditionKey = <
	Key extends string,
	Add extends Record<Language, RecordObject>,
	Value extends Record<Language, RecordObject>,
>(
	key: string,
	add: Add,
	value: Value,
): Record<Language, Add & Value & { [key in keyof Key]: string }> =>
	languages.reduce((acc, language) => {
		acc[language] = {
			...(value?.[language] || {}),
			[key]: add?.[language],
		};
		return acc;
	}, createObjectFromConstantByType<any>());

export const addToObjectByAdditionKey = <
	Key extends string,
	Add extends Record<Language, RecordObject>,
	Value extends RecordObject,
>(
	key: string,
	add: Add,
	value: Value,
): Record<Language, Add & Value & { [key in keyof Key]: string }> =>
	languages.reduce((acc, language) => {
		acc[language] = {
			...(value || {}),
			[key]: add?.[language],
		};
		return acc;
	}, createObjectFromConstantByType<any>());

export const addToObjectByAdditionKeyAndObject = <
	Key extends string,
	Add extends Record<Language, RecordObject>,
	Value extends RecordObject,
	Obj extends Record<Language, RecordObject>,
>(
	key: Key,
	add: Add,
	value: Value,
	obj: Obj,
): Record<Language, Add & Value & Obj & { [key in keyof Key]: string }> =>
	languages.reduce((acc, language) => {
		acc[language] = {
			...(value || {}),
			...(obj?.[language] || {}),
			[key]: add?.[language],
		};
		return acc;
	}, createObjectFromConstantByType<any>());

export const pickFromObjectByAdditionKey = <
	Value extends Record<Language, RecordObject>,
>(
	key: string,
	value?: Value,
): Record<Language, string> =>
	languages.reduce((acc, language) => {
		acc[language] = value?.[language]?.[key] || "";
		return acc;
	}, createObjectFromConstantByType<any>());

// const lang = useTypedSelector((state) => state.session.language);
export const findValueByLanguageKey = (
	obj?: Partial<Record<Language, string>>,
): string | undefined => {
	if (!obj) return undefined;

	let foundValue: string | undefined;

	languages.forEach((lang) => {
		if (obj?.[lang] !== undefined) {
			foundValue = obj[lang];
		}
	});

	if (!foundValue) return undefined;
	if (typeof foundValue === "string") return foundValue?.trim();
	return foundValue;
};

export const findValuesByLanguageKey = (
	obj?: Partial<Record<Language, string>>,
): Partial<Record<Language, string | undefined>>[] => {
	const foundValues: Partial<Record<Language, string | undefined>>[] = [];

	languages.forEach((lang) => {
		const value = {
			[lang]: obj?.[lang],
		};
		foundValues.push(value);
	});

	return foundValues;
};

export const getValueByLanguageKey = <Value = any>(data: {
	value: Partial<Record<Language, any>>;
	language?: Language;
}): Value | undefined => {
	const { value, language } = data;

	if (!value) return undefined;

	let foundValue = language ? value?.[language] : undefined;

	languages.forEach((lang) => {
		if (value?.[lang] !== undefined && !foundValue) {
			foundValue = value[lang];
		}
	});
	return foundValue;
};

const MAP_OBJECT_LANG_PICK_FIELDS = [
	"title",
	"street",
	"settlement",
	"name",
] as const;

const MAP_OBJECT_LANG_MERGE_FIELDS = [
	"title",
	"street",
	"settlement",
	"name",
] as const;

const buildMapLangTryOrder = (
	preferred: Language,
	bag: Partial<Record<Language, unknown>>,
): string[] => {
	const tryOrder: string[] = [
		preferred,
		ConstantLanguage.EN,
		...MAP_LANGUAGES,
		...Object.keys(bag),
	];
	const seen = new Set<string>();
	return tryOrder.filter((lang) => {
		if (!lang || seen.has(lang)) return false;
		seen.add(lang);
		return true;
	});
};

const mergeEmptyMapObjectLangFields = <Value extends MapObjectLanguageFields>(
	bag: Partial<Record<Language, Value>>,
	fields: Value,
	tryOrder: string[],
): Value => {
	const result = { ...fields };
	for (const field of MAP_OBJECT_LANG_MERGE_FIELDS) {
		if (!String(result[field] ?? "").trim()) {
			for (const lang of tryOrder) {
				const value = bag[lang as Language]?.[field];
				if (String(value ?? "").trim()) {
					result[field] = value;
					break;
				}
			}
		}
	}
	return result;
};

export type MapObjectLanguageFields = {
	country?: string;
	countryCode?: string;
	settlement?: string;
	settlementClass?: string;
	settlementType?: string;
	district?: string;
	region?: string;
	sector?: { id: number; name: Record<Language, string> } | null;
	street?: string;
	streetType?: string;
	streetHouses?: { id: string; number: string }[];
	name?: string;
	number?: string;
	house?: string;
	entrance?: string;
	customHouse?: string;
	customStreet?: string;
	title?: string;
	address?: string;
};

const mapObjectLangFieldsHasContent = (
	fields: Record<string, unknown> | null | undefined,
): boolean => {
	if (!fields || typeof fields !== "object") return false;
	return MAP_OBJECT_LANG_PICK_FIELDS.some(
		(field) => String(fields[field] ?? "").trim().length > 0,
	);
};

const normalizeMapLanguage = (lang?: Language | null): Language => {
	if (!lang) return ConstantLanguage.EN;
	const key = String(lang).trim().toLowerCase();
	if (key === "ua") return ConstantLanguage.UK;
	if (languages.includes(key as Language)) return key as Language;
	return ConstantLanguage.EN;
};

/** Map object language bag: UI lang → en → first non-empty. */
export const pickMapObjectLanguageFields = <
	Value extends MapObjectLanguageFields = MapObjectLanguageFields,
>(
	bag: Partial<Record<Language, Value>> | null | undefined,
	preferredLang?: Language | null,
): { fields: Value; lang: Language } | null => {
	if (!bag || typeof bag !== "object") return null;

	const preferred = normalizeMapLanguage(preferredLang);
	const tryOrder = buildMapLangTryOrder(preferred, bag);

	let pickedFields: Value | null = null;
	for (const lang of tryOrder) {
		const fields = bag[lang as Language];
		if (mapObjectLangFieldsHasContent(fields)) {
			pickedFields = fields as Value;
			break;
		}
	}

	const base =
		pickedFields ??
		bag[preferred] ??
		bag[ConstantLanguage.EN] ??
		null;
	if (!base) return null;

	const merged = mergeEmptyMapObjectLangFields(bag, base, tryOrder);
	return { fields: merged, lang: preferred };
};

export const resolveMapObjectLanguageFields = <
	Value extends MapObjectLanguageFields = MapObjectLanguageFields,
>(
	bag: Partial<Record<Language, Value>> | null | undefined,
	preferredLang?: Language | null,
): Value | undefined => pickMapObjectLanguageFields(bag, preferredLang)?.fields;

/** Resolves role name for display (handles both string and Record<Language, string>) */
export const getRoleDisplayName = (
	name: string | Record<Language, string> | undefined,
	language?: Language,
): string => {
	if (typeof name === "string") return name;
	if (!name || typeof name !== "object") return "";
	return (
		getValueByLanguageKey({ value: name, language }) ??
		Object.values(name).find((v) => v != null && v !== "") ??
		""
	);
};
